import { useEffect, useState, useCallback } from "react";
import { WorkspaceMember, WorkspaceMemberSchema } from "../models/workspace";
import { fetchFastApi } from "../api/fastapi";
import { z } from "zod";
import { toast } from "sonner";
import { UserSchema } from "../models/user";

interface UseWorkspaceMembersProps {
  fetchOnLoad?: boolean;
}

export const useWorkspaceMembers = ({
  fetchOnLoad = true,
}: UseWorkspaceMembersProps) => {
  const [workspaceMembers, setWorkspaceMembers] = useState<WorkspaceMember[]>(
    [],
  );
  const [users, setUsers] = useState<z.infer<typeof UserSchema>[]>([]);
  const [loading, setLoading] = useState(false);
  // The member read's own failure, beside its `loading`. A surface rendering the
  // list needs it to tell a failed read from an empty workspace: the toast says
  // a read failed somewhere, not that the list in front of the user is unknown.
  const [error, setError] = useState<Error | null>(null);
  const fetchWorkspaceMembers = async () => {
    setLoading(true);
    try {
      const data = await fetchFastApi({
        path: "/workspace/members",
        method: "GET",
      });
      if (data.workspaceMembers) {
        const members = WorkspaceMemberSchema.array().parse(
          data.workspaceMembers,
        );
        setWorkspaceMembers(members);
      }
      if (data.users) {
        const users = UserSchema.array().parse(data.users);
        setUsers(users);
      }
      setError(null);
    } catch (error) {
      console.error(error);
      setError(error instanceof Error ? error : new Error(String(error)));
      toast.error("Error fetching workspace members");
    } finally {
      setLoading(false);
    }
  };

  const handleRoleChange = useCallback(
    async (memberId: string, newRoleId: string) => {
      let prevMembers: WorkspaceMember[] = [];
      try {
        setWorkspaceMembers((prev) => {
          prevMembers = prev;
          return prev.map((member) =>
            member.id === memberId ? { ...member, roleId: newRoleId } : member,
          );
        });
        const res = await fetchFastApi({
          path: `/workspace/members/${memberId}`,
          method: "PUT",
          body: { role_id: newRoleId },
        });
        if (res.error) {
          setWorkspaceMembers(prevMembers);
          toast.error(res.error);
          return;
        }

        toast.success("Role updated successfully");
      } catch (error) {
        setWorkspaceMembers(prevMembers);
        console.error(error);
        toast.error("An error occurred while updating the role");
      }
    },
    [],
  );

  // Removes the member from this workspace only: their account and their other
  // workspaces are untouched. Returns whether the removal succeeded so the
  // caller can close its confirmation.
  const handleRemoveMember = useCallback(
    async (memberId: string, userId: string): Promise<boolean> => {
      try {
        const res = await fetchFastApi({
          path: `/workspace/members/${memberId}`,
          method: "DELETE",
        });
        if (res.error) {
          toast.error(res.error);
          return false;
        }
        setWorkspaceMembers((prev) => prev.filter((m) => m.id !== memberId));
        setUsers((prev) => prev.filter((u) => u.id !== userId));
        toast.success("Member removed from workspace");
        return true;
      } catch (error) {
        console.error(error);
        toast.error(
          error instanceof Error
            ? error.message
            : "An error occurred while removing the member",
        );
        return false;
      }
    },
    [],
  );

  const handleReactivateUser = useCallback(async (userId: string) => {
    let prevUsers: z.infer<typeof UserSchema>[] = [];
    try {
      setUsers((prev) => {
        prevUsers = prev;
        return prev.map((u) =>
          u.id === userId ? { ...u, deactivatedAt: null } : u,
        );
      });
      const res = await fetchFastApi({
        path: `/user/users/${userId}/reactivate`,
        method: "POST",
      });
      if (res.error) {
        setUsers(prevUsers);
        toast.error(res.error);
        return;
      }
      toast.success("User reactivated");
    } catch (error) {
      setUsers(prevUsers);
      if (error instanceof Error) {
        toast.error(error.message);
      } else {
        toast.error("An error occurred while reactivating the user");
      }
    }
  }, []);

  useEffect(() => {
    if (fetchOnLoad) {
      fetchWorkspaceMembers();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    workspaceMembers,
    users,
    loading,
    error,
    fetchWorkspaceMembers,
    handleRoleChange,
    handleRemoveMember,
    handleReactivateUser,
  };
};
